import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
  DateRangePickerProps,
  TableProps,
  ExpandableSection,
  Icon,
  StatusIndicator,
} from '@cloudscape-design/components';

import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { PropertyDateFilter } from '../../common/filtering/date-filtering';
import { ResourceError } from '../Loading';
import { exportExcel } from '../../common/excel';
import { PropertyFilterOption, PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { PlaceReportWithContacts as PlaceReport } from '../../common/types';
import { getReportsMeta } from '../../common/api/reports';
import { getReports } from '../../common/api/reports';
import { PlaceStatus } from '../PlaceStatus';
import {
  formatDateString,
  formatDateStringWithTime,
  formatTime,
} from '../../common/formatting/dateFormat';
import { PlacePeriodList } from '../PlacePeriodList';
import { PlaceReportContactsList } from '../PlaceReportContactsList';
import { TextQueryFilter } from '../TextQueryFilter';
import { StarsIcon } from '../StarsIcon';
import { PlaceContactsList } from '../PlaceContactsList';
import { PlaceReportPlaceContactsList } from '../PlaceReportPlaceContactsList';
import { PhoneNumber } from '../PhoneNumber';
import { Email } from '../Email';

export type ReportTableProps = {
  now: Date;
  setNow: (now: Date) => void;
  query: PropertyFilterQuery;
  setQuery?: (query: PropertyFilterQuery) => void;
  range?: DateRangePickerProps.Value | null;
  setRange?: (range: DateRangePickerProps.Value | null) => void;
  sortingField?: string;
  setSortingField?: (field: string | undefined) => void;
  sortDirection?: 'asc' | 'desc';
  setSortDirection?: (direction: 'asc' | 'desc') => void;
  hideActions?: boolean;
  hideFilter?: boolean;
  hidePagination?: boolean;
  hideColumns?: string[];
  variant?: 'embedded' | 'borderless' | 'stacked' | 'full-page';
  additionalActions?: React.ReactNode;
  selectedItems?: PlaceReport[];
  setSelectedItems?: (items: PlaceReport[]) => void;
  textQuery?: string;
  setTextQuery?: (text: string) => void;
};

export function getUserReportTypeLabel(reportType?: string | null): string {
  return reportType === 'public_link' ? 'Public Link' : 'Internal';
}

export function ReportTable(props: ReportTableProps) {
  const {
    now,
    setNow,
    query,
    setQuery,
    range,
    setRange,
    selectedItems,
    setSelectedItems,
    sortingField,
    setSortingField,
    sortDirection,
    setSortDirection,
    textQuery,
    setTextQuery,
  } = props;
  const navigate = useNavigate();
  const resourceName = 'Reports';
  const endpointName = 'reports';
  // relative times are in the past vs future
  const past = true;

  // TODO move to context
  const DEFAULT_PREFERENCES = {
    pageSize: 50,
  };
  const PAGE_SIZE_OPTIONS = [
    { value: 10, label: `10 ${resourceName}` },
    { value: 20, label: `20 ${resourceName}` },
    { value: 50, label: `50 ${resourceName}` },
    { value: 100, label: `100 ${resourceName}` },
  ];

  const [preferences, setPreferences] = useLocalStorage<
    CollectionPreferencesProps.Preferences<unknown>
  >('React-Cards-Preferences', DEFAULT_PREFERENCES);

  const pageSize = preferences?.pageSize || DEFAULT_PREFERENCES.pageSize;

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const metaQuery = useQuery({
    queryKey: [endpointName, 'meta'],
    queryFn: getReportsMeta,
    staleTime: 360_000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  const { options, properties } = useMemo(() => {
    if (metaQuery.data) {
      return {
        options: metaQuery.data.filteringOptions as PropertyFilterOption[],
        properties: metaQuery.data.filteringProperties,
      };
    }
    return {
      options: [],
      properties: [],
    };
  }, [metaQuery.data]);

  // wait for the meta query to finish before proceeding
  const { isPending, isFetching, error, data } = useQuery({
    queryKey: [
      endpointName,
      query,
      range,
      pageSize,
      sortingField,
      sortDirection,
      page,
      now,
      textQuery,
    ],
    enabled: metaQuery.isSuccess,
    // TODO add sorting
    queryFn: async () =>
      await getReports(query, page, pageSize, range, sortingField, sortDirection, now, textQuery),
    // 1 minute
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    // placeholderData: keepPreviousData,
  });

  const loading =
    (isPending || isFetching || metaQuery.isPending || metaQuery.isFetching) &&
    !error &&
    !metaQuery.error;

  useEffect(() => {
    if (data && data.pages !== pageCount) {
      setPageCount(data.pages);
    }
  }, [data]);

  useEffect(() => {
    if (!data || !selectedItems?.length || !setSelectedItems) return;

    const currentItemsByUuid = new Map(data.items.map((item) => [item.uuid, item]));
    const reconciled = selectedItems
      .map((item) => currentItemsByUuid.get(item.uuid))
      .filter((item): item is PlaceReport => item !== undefined);
    const changed =
      reconciled.length !== selectedItems.length ||
      reconciled.some((item, index) => item !== selectedItems[index]);

    if (changed) {
      setSelectedItems(reconciled);
    }
  }, [data, selectedItems, setSelectedItems]);

  const refresh = () => {
    setNow(new Date());
    metaQuery.refetch();
  };

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={loading}
      pagesCount={pageCount}
      onChange={({ detail }) => setPage(detail.currentPageIndex)}
    />
  );

  return (
    <Table
      onSortingChange={({ detail }) => {
        setSortingField?.(detail.sortingColumn.sortingField);
        setSortDirection?.(detail.isDescending ? 'desc' : 'asc');
      }}
      sortingColumn={{
        sortingField,
      }}
      // default to ascending
      sortingDescending={sortDirection === 'desc'}
      header={
        <Header
          variant={
            !props.variant
              ? 'awsui-h1-sticky'
              : props.variant !== 'full-page'
                ? 'h2'
                : 'awsui-h1-sticky'
          }
          description="View and manage place reports derived from reviews. Each report links to a scorecard."
          actions={
            props.hideActions ? undefined : (
              <SpaceBetween size="xs" direction="horizontal">
                {props.additionalActions}
                <Button variant="normal" iconName="refresh" onClick={refresh} disabled={loading} />
                {/* TODO add export */}
                {/* <Button
                  disabled={loading}
                  iconName="download"
                  onClick={() => {
                    exportExcel(
                      (data?.items ?? []).map((item) => ({
                        name: item.place?.name,
                        address: item.place?.address,
                        rating: item.place?.rating,
                        reviews: item.place?.total_review_count,
                      })),
                      'places',
                    );
                  }}
                /> */}
              </SpaceBetween>
            )
          }
          counter={
            data?.total && metaQuery.data?.total
              ? `(${data.total.toLocaleString()}/${metaQuery.data.total.toLocaleString()})`
              : undefined
          }
        >
          {resourceName}
        </Header>
      }
      items={data?.items ?? []}
      selectedItems={selectedItems}
      onSelectionChange={
        setSelectedItems ? ({ detail }) => setSelectedItems(detail.selectedItems) : undefined
      }
      selectionType={setSelectedItems ? 'multi' : undefined}
      loading={loading}
      loadingText={`Loading ${resourceName}...`}
      pagination={props.hidePagination ? undefined : pagination}
      preferences={
        <CollectionPreferences
          disabled={loading}
          pageSizePreference={{
            options: PAGE_SIZE_OPTIONS,
          }}
          preferences={preferences}
          onConfirm={(e) => setPreferences(e.detail)}
        />
      }
      filter={
        props.hideFilter ? undefined : (
          <SpaceBetween direction="vertical" size="xxxs">
            <TextQueryFilter
              loading={loading}
              text={textQuery ?? ''}
              setText={(q) => {
                setTextQuery?.(q);
                // reset page when filtering
                setPage(1);
              }}
            />
            <ExpandableSection headerText="Additional filters">
              <PropertyDateFilter
                loading={loading}
                query={query}
                setQuery={(q) => {
                  setQuery?.(q);
                  // reset page when filtering
                  setPage(1);
                }}
                range={range ?? null}
                setRange={(r) => {
                  setRange?.(r);
                  // reset page when filtering
                  setPage(1);
                }}
                propertyFilteringOptions={options}
                filteringProperties={properties}
                past={past}
              />
            </ExpandableSection>
          </SpaceBetween>
        )
      }
      variant={props.variant ?? 'full-page'}
      stickyHeader={!props.variant || props.variant === 'full-page'}
      empty={
        <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
          {error || metaQuery.error ? (
            <ResourceError
              error={error ?? metaQuery.error ?? new Error('Unknown error')}
              refetch={refresh}
              resourceName={resourceName}
            />
          ) : (
            <SpaceBetween size="xxs">
              <div>
                <b>No {resourceName.toLowerCase()}</b>
                <Box color="inherit" variant="p">
                  No {resourceName.toLowerCase()} found.
                </Box>
              </div>
            </SpaceBetween>
          )}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'place_name',
          header: 'Place',
          minWidth: 220,
          cell: (item: PlaceReport) => (
            <Header
              variant="h2"
              description={
                <SpaceBetween direction="vertical" size="xxxs">
                  {/* `/dashboard/places/${item.place?.uuid}` */}
                  <Button
                    href={`/dashboard/places/${item.place?.uuid}`}
                    variant="inline-link"
                    target="_blank"
                    iconName="status-info"
                    wrapText={false}
                    iconAlign="left"
                  >
                    {item.place?.address}
                  </Button>
                  {/* <Link
                    href={`/dashboard/places/${item.place?.uuid}`}
                    variant="secondary"
                    target="_blank"
                    // onFollow={(e) => {
                    //   e.preventDefault();
                    //   navigate(`/dashboard/places/${item.place?.uuid}`);
                    // }}
                  >
                    {item.place?.address}
                  </Link> */}
                  <SpaceBetween direction="horizontal" size="xs">
                    {item.place?.rating?.toFixed(1) ?? '?.?'}
                    <StarsIcon stars={item.place?.rating ?? undefined} />
                    <Link href={item.place?.cid_url ?? undefined} target="_blank" variant="secondary">
                      {item.place?.total_review_count?.toLocaleString() ?? '?'} Google reviews
                    </Link>
                  </SpaceBetween>
                  <SpaceBetween size="xs" direction="horizontal">
                    <PlaceStatus
                      tasks={item.tasks}
                      periods={item.periods}
                      text={formatDateString(item.created_at)}
                    />
                    <Box variant="span" color="text-body-secondary">
                      {formatTime(new Date(item.created_at + 'Z'))}
                    </Box>
                  </SpaceBetween>
                </SpaceBetween>
              }
            >
              <Button
                href={`/${item.slug ?? item.uuid}`}
                variant="normal"
                target="_blank"
                iconName="transcript"
                wrapText
                iconAlign="left"
              >
                {item.place?.name}
              </Button>
            </Header>
          ),
        },
        {
          id: 'periods_google',
          header: 'Google Scores',
          cell: (item: PlaceReport) => (
            <PlacePeriodList
              report={item}
              name={item.place?.name ?? 'Report'}
            />
          ),
        },
        {
          id: 'periods_yelp',
          header: 'Yelp Scores',
          cell: (item: PlaceReport) => (
            <PlacePeriodList
              report={item}
              name={item.place?.name ?? 'Report'}
              source="yelp"
            />
          ),
        },
        {
          id: 'user',
          header: 'Type',
          cell: (item: PlaceReport) =>
            item.distributor ? (
              <SpaceBetween size="xxxs" direction="vertical">
                <StatusIndicator type={item.report_type === 'distributor_internal' ? 'pending' : 'in-progress'}>
                  {item.report_type === 'distributor_internal' ? 'Distributor (Internal)' : 'Distributor'}
                </StatusIndicator>
                <Button
                  href={`/dashboard/distributors/${item.distributor.uuid}`}
                  variant="inline-link"
                  iconName="user-profile"
                  iconAlign="left"
                >
                  {item.distributor.first_name} {item.distributor.last_name}
                </Button>
                <PhoneNumber
                  phoneNumber={item.distributor.phone_number}
                  lineTypeInfo={item.distributor.phone_line_type_info}
                  showLineTypeInfo
                />
                <Email email={item.distributor.email} />
                <Link
                  href={`/d/${item.distributor.path}`}
                  variant="secondary"
                  target="_blank"
                >
                  /d/{item.distributor.path}
                </Link>
              </SpaceBetween>
            ) : item.user ? (
              <SpaceBetween size="xxxs" direction="vertical">
                <StatusIndicator type={item.report_type === 'public_link' ? 'info' : 'success'}>
                  {getUserReportTypeLabel(item.report_type)}
                </StatusIndicator>
                {item.user.name !== item.user.email && <Box variant="span">{item.user.name}</Box>}
                <Box variant="span" color="text-body-secondary">
                  {item.user.email}
                </Box>
              </SpaceBetween>
            ) : item.contacts && item.contacts.length > 0 ? (
              <SpaceBetween size="xxxs" direction="vertical">
                <StatusIndicator type="info">Public-Facing</StatusIndicator>
                <PlaceReportContactsList contacts={item.contacts} showLineTypeInfo />
              </SpaceBetween>
            ) : (
              <SpaceBetween size="xxxs" direction="vertical">
                <StatusIndicator type="stopped">Drip Campaign</StatusIndicator>
              </SpaceBetween>
            ),
        },
        {
          id: 'contacts',
          header: 'Google Contacts',
          cell: (item: PlaceReport) => <PlaceReportPlaceContactsList item={item} showLineTypeInfo />,
        },
      ].filter((column) => !props.hideColumns?.includes(column.id))}
    />
  );
}
